import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { AppService } from '@app/app.service';
import { formatDecimalPrecision } from 'src/shared/helpers/number-helper';
import { FieldConfig, ITrailingButtonEvent } from '../field.interface';

@Component({
    selector: 'app-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.scss'],
})
export class InputComponent implements OnInit, AfterViewInit, OnChanges {
    readonly appService = inject(AppService);
    readonly #elementRef: ElementRef = inject(ElementRef);
    field: FieldConfig;
    group: FormGroup;
    data: { mask: string } = {
        mask: null,
    };
    outputEvent: EventEmitter<ITrailingButtonEvent> = null;

    /**
     * Событие окончания инициализации компонента
     */
    ngOnInit() {
        if (this.field.interactionField && this.field.disabled) {
            setTimeout(() => {
                this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            });
        }
        if (this.field.interactionField && this.field.required) {
            this.group.get(this.field.name).addValidators(Validators.required);
        }
        if (this.field.interactionField && this.field.minlength) {
            this.group.get(this.field.name).addValidators(Validators.minLength(+this.field.minlength));
        }
        if (this.field.interactionField && this.field.maxlength) {
            this.group.get(this.field.name).addValidators(Validators.maxLength(+this.field.maxlength));
        }
        if (this.field.interactionField && this.field.regex) {
            this.group.get(this.field.name).addValidators(Validators.pattern(this.field.regex));
        }
        if (this.field.interactionField && this.field.validations) {
            for (const validator of this.field.validations) {
                this.group.get(this.field.name)?.addValidators(validator.validator);
            }
        }
        if (this.field.disabled) {
            setTimeout(() => {
                this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            });
        }
        setTimeout(() => {
            this.group.get(this.field.name).updateValueAndValidity({ emitEvent: false });
        });
        this.applyDecimalPrecision();
    }

    /**
     * Событие изменения данных
     * @param changes изменения
     */
    ngOnChanges(changes: SimpleChanges) {
        if (changes.field) {
            this.applyDecimalPrecision();
        }
    }

    /**
     * Установка запрета изменения поля
     * @param disabled значение флага
     */
    setDisabled(disabled) {
        this.field.disabled = disabled;
        if (this.field.disabled) {
            this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
        } else {
            this.group.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
        }
    }

    ngAfterViewInit(): void {
        const inputRef = this.#elementRef.nativeElement.querySelector('input') as HTMLElement;
        if (this.field?.autoFocus) {
            if (inputRef && inputRef?.clientHeight && inputRef?.clientWidth) {
                setTimeout(() => {
                    inputRef.focus();
                });
            }
        }
    }

    /**
     * Форматирует значение числового поля при потере фокуса, добавляя недостающие десятичные знаки.
     *
     * - Если поле пустое, функция ничего не делает.
     * - Если уже есть десятичная часть, дополняет её нулями до указанной точности.
     */
    applyDecimalPrecision() {
        const target = this.group.get(this.field.name);
        if (!target?.value) {
            return;
        }

        if (this.field.mask === 'integer') {
            target.setValue(formatDecimalPrecision(target.value, 0));
        }

        if (this.field.mask === 'float') {
            target.setValue(formatDecimalPrecision(target.value, this.field.precision ?? 2));
        }
    }
}
